/**
 * Parse large JSON off the main thread so the UI stays responsive.
 * @param {string} text
 */
export function parseJsonAsync(text) {
  if (!text) return Promise.resolve({});

  if (typeof Worker === 'undefined' || text.length < 512_000) {
    return Promise.resolve(JSON.parse(text));
  }

  return new Promise((resolve, reject) => {
    const workerSource = `
      self.onmessage = (event) => {
        try {
          self.postMessage({ ok: true, json: JSON.parse(event.data) });
        } catch (error) {
          self.postMessage({ ok: false, error: error.message || 'Invalid JSON' });
        }
      };
    `;
    const blob = new Blob([workerSource], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);

    worker.onmessage = (event) => {
      worker.terminate();
      URL.revokeObjectURL(url);
      if (event.data.ok) resolve(event.data.json);
      else reject(new Error(event.data.error));
    };

    worker.onerror = (error) => {
      worker.terminate();
      URL.revokeObjectURL(url);
      reject(error);
    };

    worker.postMessage(text);
  });
}

/**
 * @param {Response} res
 * @param {(received: number, total: number) => void} [onProgress]
 */
export async function readResponseText(res, onProgress) {
  if (!res.body) {
    const text = await res.text();
    onProgress?.(text.length, text.length);
    return text;
  }

  const total = Number(res.headers.get('content-length') || 0);
  const reader = res.body.getReader();
  const chunks = [];
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress?.(received, total);
  }

  const merged = new Uint8Array(received);
  let offset = 0;
  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return new TextDecoder().decode(merged);
}

/** Table row shape — omits heavy editable_quotas from React state. */
export function slimQuotaUser(user) {
  return {
    wh_mo_id: user.wh_mo_id,
    cnt_id: user.cnt_id,
    wh_flag: user.wh_flag,
    age: user.age,
    gender: user.gender,
    selected_cnq_ids: user.selected_cnq_ids ?? [],
    user_termination: user.user_termination ?? null,
    quota_data: user.quota_data ?? [],
  };
}

/**
 * @param {import('./quotaUserTypes.js').QuotaUser[]} users
 */
export function buildQuotaUserStore(users) {
  const fullById = new Map();
  const slimUsers = users.map((user) => {
    fullById.set(user.wh_mo_id, user);
    return slimQuotaUser(user);
  });
  return { slimUsers, fullById };
}

export function getFullQuotaUser(fullById, whMoId) {
  return fullById.get(whMoId) ?? null;
}
